import React from 'react';
import { motion } from 'framer-motion';
import { Home, RefreshCw, KeyRound, ArrowRight, Star, Calculator } from 'lucide-react';
interface IntentSelectorProps {
  onSelect: (intent: 'purchase' | 'refinance' | 'equity') => void;
}
export function IntentSelector({
  onSelect
}: IntentSelectorProps) {
  return <div className="max-w-5xl mx-auto" data-id="element-63">
      {/* Eyebrow badge — makes it crystal clear this is a calculator */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex justify-center mb-5" data-id="element-64">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-forest text-lime-300 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-65">
          <Calculator className="w-3.5 h-3.5" data-id="element-66" />
          AI Mortgage Calculator
        </div>
      </motion.div>

      {/* Headline */}
      <motion.h1 initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} className="text-center text-4xl sm:text-5xl md:text-6xl font-extrabold text-forest tracking-tight leading-[1.05] mb-4 lowercase" data-id="element-67">
        see what you can afford{' '}
        <span className="text-coral" data-id="element-68">in 60 seconds</span>
      </motion.h1>

      <motion.p initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="text-center text-base sm:text-lg text-forest/70 max-w-2xl mx-auto mb-3" data-id="element-69">
        Instant Mortgage Calculator —{' '}
        <span className="text-coral font-semibold" data-id="element-70">
          No SSN. No hard credit pull. No rate funnel.
        </span>{' '}
        Just answers, fast.
      </motion.p>

      {/* Social proof */}
      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.15
    }} className="flex items-center justify-center gap-3 mb-10" data-id="element-71">
        <div className="flex -space-x-2" data-id="element-72">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cream-deep to-forest-100 border-2 border-cream" data-id="element-73" />
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-coral-100 to-coral-200 border-2 border-cream" data-id="element-74" />
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-lime-100 to-lime-200 border-2 border-cream" data-id="element-75" />
        </div>
        <div className="flex items-center gap-1.5" data-id="element-76">
          <div className="flex gap-0.5" data-id="element-77">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-coral text-coral" data-id="element-78" />)}
          </div>
          <span className="text-xs font-semibold text-forest/70" data-id="element-79">
            <span className="text-forest font-bold" data-id="element-80">2,800+</span> five-star
            reviews
          </span>
        </div>
      </motion.div>

      {/* Section label */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.2
    }} className="text-center mb-5" data-id="element-81">
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-forest/50" data-id="element-82">
          Choose your calculator
        </span>
      </motion.div>

      {/* Three calculator cards */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.25
    }} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-83">
        <IntentCard label="Home Equity" calcLabel="Home Equity Calculator" subtitle="See how much cash you can access" icon={<Home className="w-6 h-6" data-id="element-85" />} variant="coral" onClick={() => onSelect('equity')} data-id="element-84" />
        <IntentCard label="Refinance" calcLabel="Refinance Calculator" subtitle="See your potential savings" icon={<RefreshCw className="w-6 h-6" data-id="element-87" />} variant="forest" onClick={() => onSelect('refinance')} data-id="element-86" />
        <IntentCard label="Purchase" calcLabel="Affordability Calculator" subtitle="See what you can afford" icon={<KeyRound className="w-6 h-6" data-id="element-89" />} variant="lime" onClick={() => onSelect('purchase')} data-id="element-88" />
      </motion.div>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-forest/60" data-id="element-90">
        <span className="flex items-center gap-1.5" data-id="element-91">
          <span className="w-1.5 h-1.5 rounded-full bg-lime-500" data-id="element-92" />
          No credit check
        </span>
        <span className="flex items-center gap-1.5" data-id="element-93">
          <span className="w-1.5 h-1.5 rounded-full bg-lime-500" data-id="element-94" />
          Takes under 2 minutes
        </span>
        <span className="flex items-center gap-1.5" data-id="element-95">
          <span className="w-1.5 h-1.5 rounded-full bg-lime-500" data-id="element-96" />
          Your info stays private
        </span>
      </div>
    </div>;
}
interface IntentCardProps {
  label: string;
  calcLabel: string;
  subtitle: string;
  icon: React.ReactNode;
  variant: 'coral' | 'forest' | 'lime';
  onClick: () => void;
}
function IntentCard({
  label,
  calcLabel,
  subtitle,
  icon,
  variant,
  onClick
}: IntentCardProps) {
  const variants = {
    coral: {
      bg: 'bg-coral',
      iconBg: 'bg-coral-500'
    },
    forest: {
      bg: 'bg-forest-500',
      iconBg: 'bg-forest-700'
    },
    lime: {
      bg: 'bg-lime-500',
      iconBg: 'bg-lime-700'
    }
  };
  const v = variants[variant];
  return <motion.button whileHover={{
    y: -3
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className={`group ${v.bg} text-white rounded-2xl p-5 text-left flex flex-col gap-4 transition-shadow hover:shadow-xl`} data-id="element-97">
      <div className="flex items-start justify-between gap-3" data-id="element-98">
        <div className="flex-1 min-w-0" data-id="element-99">
          <span className="inline-block text-[10px] uppercase tracking-wider font-bold text-white/80 bg-white/15 px-2 py-0.5 rounded-full mb-2" data-id="element-100">
            {calcLabel}
          </span>
          <h3 className="text-2xl font-extrabold lowercase tracking-tight leading-none" data-id="element-101">
            {label}
          </h3>
        </div>
        <div className={`w-11 h-11 ${v.iconBg} text-white rounded-full flex items-center justify-center shrink-0`} data-id="element-102">
          {icon}
        </div>
      </div>

      <p className="text-sm text-white/90 font-medium leading-snug" data-id="element-103">
        {subtitle}
      </p>

      <div className="mt-auto flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/90 group-hover:gap-2.5 transition-all" data-id="element-104">
        Open calculator
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-105" />
      </div>
    </motion.button>;
}